<template>
  <div
    class="tabs"
    :class="[type, type + '-' + size, 'tabs-' + direction]"
    role="tablist"
    :aria-orientation="direction === 'vertical' ? 'vertical' : 'horizontal'"
  >
    <div
      v-for="tab in tabs"
      :key="tab.id"
      :ref="el => setTabRef(el as HTMLElement, tab.id)"
      v-tippy="{ content: showTooltip ? tab.label : null }"
      role="tab"
      :tabindex="tab.isHidden ? -1 : 0"
      :aria-selected="tab.id === modelValue"
      class="tab"
      :class="{ active: tab.id === modelValue, '!hidden': tab.isHidden }"
      @click="makeActive(tab)"
      @keydown.enter.prevent="makeActive(tab)"
      @keydown.space.prevent="makeActive(tab)"
    >
      <Icon
        v-if="tab.icon"
        :type="tab.icon"
      />
      <span
        v-if="showLabel"
        class="tab-label"
      >
        {{ tab.label }}
      </span>
      <Badge
        rounded
        class="ml-2"
        size="sm"
        :content="tab.counter > 0 ? tab.counter : ''"
        inline
      ></Badge>
    </div>

    <!-- Animated underline for underlined variants -->
    <div
      v-if="isUnderlinedVariant"
      class="animated-underline"
      :style="underlineStyle"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import Icon from '@/components/icon/Icon.vue'
import Badge from '@/components/badge/Badge.vue'

interface Tab {
  id: number
  label: string
  isHidden: boolean
  icon: string
  counter: number
}

const props = withDefaults(
  defineProps<{
    modelValue: string | number | null
    type?: string
    size?: string
    direction?: 'horizontal' | 'vertical'
    tabs: Tab[]
    initialTab?: string | null
    showLabel?: boolean
    showTooltip?: boolean
  }>(),
  {
    modelValue: null,
    type: 'underlined',
    size: 'md',
    direction: 'horizontal',
    tabs: () => [],
    initialTab: null,
    showLabel: true,
    showTooltip: false
  }
)

const emit = defineEmits(['update:modelValue'])

// Reactive data for animation
const tabRefs = ref<Map<number, HTMLElement>>(new Map())
const underlineStyle = ref({
  width: '0px',
  left: '0px',
  opacity: '0'
})

/**
 * Check if the current tab type is an underlined variant
 */
const isUnderlinedVariant = computed(() => {
  return props.type === 'underlined' || props.type === 'underlined-small' || props.type === 'underlined-block'
})

/**
 * Set tab reference for DOM element access
 * @param el - The DOM element
 * @param tabId - The tab ID
 */
function setTabRef(el: HTMLElement | null, tabId: number) {
  if (el) {
    tabRefs.value.set(tabId, el)
  } else {
    tabRefs.value.delete(tabId)
  }
}

/**
 * Update the animated underline position and size
 */
async function updateUnderlinePosition() {
  if (!isUnderlinedVariant.value || !props.modelValue) {
    underlineStyle.value.opacity = '0'
    return
  }

  await nextTick()

  const activeTabElement = tabRefs.value.get(props.modelValue as number)
  if (!activeTabElement) {
    underlineStyle.value.opacity = '0'
    return
  }

  const tabsContainer = activeTabElement.parentElement
  if (!tabsContainer) return

  const containerRect = tabsContainer.getBoundingClientRect()
  const tabRect = activeTabElement.getBoundingClientRect()

  const left = tabRect.left - containerRect.left
  const width = tabRect.width

  underlineStyle.value = {
    width: `${width}px`,
    left: `${left}px`,
    opacity: '1'
  }
}

/**
 * Make a tab active and emit the change
 * @param tab - The tab to activate
 */
function makeActive(tab: Tab) {
  emit('update:modelValue', tab.id)
}

// Watch for changes in modelValue to update underline position
watch(() => props.modelValue, updateUnderlinePosition, { immediate: true })

// Update underline position when component mounts
onMounted(() => {
  updateUnderlinePosition()
})
</script>

<style>
.tabs {
  user-select: none;
}

.tabs.tabs-vertical.pills-rail,
.tabs.tabs-vertical.pills-rail-block {
  flex-direction: column;
}

.tabs.pills-rail,
.tabs.pills-rail-block {
  background-color: var(--bg-secondary_alt);
  border-radius: var(--rounded-lg);
  display: inline-flex;
  border: 1px solid var(--border-secondary);
  /* box-shadow: var(--shadow-md); */
}

.tabs.pills-rail > .tab,
.tabs.pills-rail-block > .tab {
  color: var(--text-tertiary);
  font-weight: var(--font-medium);
  padding: var(--s-3) var(--s-4);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--rounded-lg);
}
.tabs.pills-rail-xs > .tab,
.tabs.pills-rail-block-xs > .tab {
  padding: var(--s-1) var(--s-2);
  font-size: var(--text-sm);
}
.tabs.pills-rail-sm > .tab,
.tabs.pills-rail-block-sm > .tab {
  padding: var(--s-2) var(--s-3);
  font-size: var(--text-sm);
}
.tabs.tabs-vertical.pills-rail > .tab,
.tabs.tabs-vertical.pills-rail-block > .tab {
  justify-content: left;
}
.tabs.pills-rail-block {
  justify-content: space-between;
  display: flex;
}
.tabs.pills-rail-block:not(.tabs-vertical) > .tab {
  width: calc(100% - 6px);
}

.tabs.pills-rail > .tab > .tab-label,
.tabs.pills-rail-block > .tab > .tab-label {
  margin-left: 5px;
}

.tabs.pills-rail > .tab.active,
.tabs.pills-rail-block > .tab.active {
  background: var(--bg-primary_alt);
  box-shadow: var(--shadow-sm);
  color: var(--text-secondary);
}

.tabs.pills-rail > .tab:focus,
.tabs.pills-rail-block > .tab:focus {
  box-shadow: var(--focus-ring-brand);
}

/* underlined-small */
.tabs.underlined-small {
  display: flex;
  align-items: center;
  flex-direction: row;
  padding: 0px;
}

.tabs.underlined-small > .tab {
  padding: 10px 10px;
  border-radius: 5px;
}

/* Remove left padding from the first tab item */
.tabs.underlined-small > .tab:first-child {
  padding-left: 0;
}

/* Remove right padding from the last tab item */
.tabs.underlined-small > .tab:last-child {
  padding-right: 0;
}

.tabs.underlined-small > .tab > .icon {
  margin-right: 5px;
}

.tabs.underlined-small > .tab.active {
  color: var(--text-brand);
}

/* Remove static underline for animated version */

/* underlined */
.tabs.underlined {
  display: flex;
  align-items: center;
  flex-direction: row;
  padding: 0px;
  font-size: var(--text-md);
  /* border-bottom: 1px solid var(--border-secondary); */
  margin-bottom: var(--s-4);
}

.tabs.underlined > .tab {
  padding: 12px 16px;
  font-weight: var(--font-bold);
  position: relative;
  text-align: center;
  color: var(--text-quaternary);
}

.tabs.underlined > .tab > .icon {
  margin-right: 8px;
}

.tabs.underlined > .tab.active {
  color: var(--text-brand-light);
}

/* Remove static underline for animated version */

/* underlined-block */
.tabs.underlined-block {
  display: flex;
  align-items: center;
  flex-direction: row;
  padding: 0px;
  justify-content: space-between;
}

.tabs.underlined-block > .tab {
  padding: 16px 20px;
  border-radius: 8px;
  width: calc(100% - 6px);
  text-align: center;
}

/* Remove left padding from the first tab item */
.tabs.underlined-block > .tab:first-child {
  padding-left: 0;
}

/* Remove right padding from the last tab item */
.tabs.underlined-block > .tab:last-child {
  padding-right: 0;
}

.tabs.underlined-block > .tab > .icon {
  margin-right: 8px;
}

.tabs.underlined-block > .tab.active {
  color: var(--text-brand);
}

/* Remove static underline for animated version */

.tab:hover {
  cursor: pointer;
  color: var(--fg-brand);
}

/* Animated underline styles */
.animated-underline {
  position: absolute;
  bottom: 0;
  height: 0.25rem;
  background-color: var(--bg-brand-solid);
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

/* Specific heights for different underlined variants */
.tabs.underlined-small .animated-underline {
  height: 2px;
}

.tabs.underlined-block .animated-underline {
  height: 3px;
  background-color: var(--bg-brand);
}

/* Ensure tabs container has relative positioning for absolute underline */
.tabs.underlined,
.tabs.underlined-small,
.tabs.underlined-block {
  position: relative;
}

/* .tabs.horizontal.pills {
  display: flex;
  align-items: center;
  flex-direction: row;
}

.tabs.pills>.tab {
  padding: 10px 20px;
}

.tabs.pills>.tab>.icon {
  margin-right: 5px;
}

.tabs.pills>.tab.active {
  background-color: var(--fg-brand);
  color: white;
  border-radius: var(--rounded-md);
}

.tab:hover {
  cursor: pointer;
  background-color: var(--extra-light);
} */
</style>
